import {
  getActiveSession,
  principalKeyOf,
  useSessionStore,
} from "@auth/sessionStore";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";

import { getConfig } from "../config";

/** Query parameters redacted by name, whatever their value looks like.
 *
 * Brian's routes are all static paths (`/`, `/tasks`, `/replay`, …) and record
 * identifiers travel in the query string instead — `?visit=…` on routes,
 * `?subject=…` on API calls. Those land verbatim in `view.url` and
 * `resource.url`, so the query string is the PHI surface here rather than path
 * segments. */
const REDACTED_QUERY_PARAMS = new Set([
  "visit",
  "patient",
  "subject",
  "record",
  "note",
]);

const REDACTED = "REDACTED";

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

/** Separate instances on purpose: `test()` against a `g`-flagged regex advances
 * its `lastIndex`, which would make the check pass on every other value. */
const UUID_TEST = new RegExp(UUID, "i");
const UUID_REPLACE_ALL = new RegExp(UUID, "gi");

/** The by-name half of `scrubUrl`'s rules, applied to text no `URL` can parse:
 * the same param names plus `x-amz-*` credential material, matched as
 * substrings. Built from the set above so there is one list to maintain. A
 * value ends at a query separator, whitespace, or the `]` that closes a LoAF
 * `TAG[src=…]` element identifier. */
const TEXT_PARAM_REPLACE_ALL = new RegExp(
  `\\b(${[...REDACTED_QUERY_PARAMS].join("|")}|x-amz-[a-z0-9-]*)=[^&\\s\\]]*`,
  "gi",
);

/** Detach handle for the session subscription, so repeat `initRum()` calls
 * replace the subscriber instead of stacking another one on top. */
let unsubscribePrincipal: (() => void) | undefined;

/** Strips identifiers out of a URL while keeping its shape intact, so views
 * still group by page and resources stay recognizable. */
export function scrubUrl(rawUrl: string): string {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    // Not an absolute URL — nothing safe to parse, so drop it entirely rather
    // than pass through something unexamined.
    return REDACTED;
  }

  for (const [key, value] of Array.from(url.searchParams)) {
    // Matched by name, or by the value being a uuid under any key at all. That
    // second rule is what keeps this correct without maintenance: a param added
    // later (`assigned_to`, a cursor encoding a record id) is covered the day it
    // appears rather than the day someone remembers this list exists.
    //
    // `x-amz-*` params are a presigned S3 URL's credential material. Stripping
    // the path uuid happens to invalidate the signature, but that is incidental
    // — redacting the signature itself is the rule that makes a captured URL
    // unusable on purpose.
    const lowerKey = key.toLowerCase();
    if (
      REDACTED_QUERY_PARAMS.has(lowerKey) ||
      lowerKey.startsWith("x-amz-") ||
      UUID_TEST.test(value)
    ) {
      url.searchParams.set(key, REDACTED);
    }
  }

  // Path uuids too: API resource URLs are uuid-keyed (`/visits/<uuid>/tasks`),
  // and a Brian route may become parameterized later without anyone revisiting
  // this file.
  url.pathname = url.pathname.replace(UUID_REPLACE_ALL, REDACTED);

  return url.toString();
}

/** Strips identifiers out of free text a URL can be embedded in — error
 * messages, stack traces, and LoAF invokers (`VIDEO[src=…].onloadedmetadata`),
 * none of which `scrubUrl` can parse. Applies the same rules by substring
 * instead: uuids anywhere, and the redacted param names by name, so a URL
 * arriving through text is covered the same way as one on a URL field. */
export function scrubText(text: string): string {
  return text
    .replace(UUID_REPLACE_ALL, REDACTED)
    .replace(TEXT_PARAM_REPLACE_ALL, `$1=${REDACTED}`);
}

/** Attributes the current RUM session to a principal — ids only.
 *
 * `usr.id` is the principal key rather than the raw `userUuid` because a
 * clinic-scoped session has no user: omitting `id` would count those sessions
 * as anonymous, and substituting `clinicUuid` would mix them into user-id
 * counts. The raw ids ride along as extra attributes so RUM can still be joined
 * against backend logs on `@usr.userId`.
 *
 * Deliberately never sets `name` or `email`, which Datadog supports and the
 * main web app also leaves unset: staff names are PHI-adjacent and buy nothing
 * a uuid doesn't (WEB-3316). `rum.test.ts` guards that. */
function syncPrincipal(): void {
  const session = getActiveSession();

  if (!session) {
    datadogRum.clearUser();
    return;
  }

  datadogRum.setUser({
    id: principalKeyOf(session),
    userId: session.userUuid,
    clinicId: session.clinicUuid,
    scope: session.scope,
  });
}

/** Initializes Datadog RUM for the Brian frontend (WEB-3316).
 *
 * Brian's UI is clinical — transcripts, notes, patient and client names, and
 * PIMS screenshots in the audit panel — so this captures as little of the page
 * as it can while staying useful: text nodes and images masked in Session
 * Replay (`<video src>` is the one element `mask` skips, so `PimsReplayPlayer`
 * carries `data-dd-privacy="hidden"` itself), action names masked, record
 * identifiers scrubbed out of URLs on the way out.
 *
 * Safe to call unconditionally; no-ops unless the environment's config enables
 * it. */
export function initRum(): void {
  const { apiUrl, datadog } = getConfig();

  // Detached up front, not after the guard: this function reflects whatever the
  // current config says, so a call that finds RUM disabled leaves nothing
  // attributing sessions behind it.
  unsubscribePrincipal?.();
  unsubscribePrincipal = undefined;

  if (!datadog.enabled || !datadog.applicationId || !datadog.clientToken) {
    return;
  }

  datadogRum.init({
    applicationId: datadog.applicationId,
    clientToken: datadog.clientToken,
    site: datadog.site,
    service: datadog.service,
    env: datadog.env,
    version: import.meta.env.VITE_APP_VERSION,

    sessionSampleRate: datadog.sessionSampleRate,
    sessionReplaySampleRate: datadog.sessionReplaySampleRate,

    // `mask` in every environment, including local: masks text nodes, image
    // sources (`IMG`/`SOURCE` — not `VIDEO[src]`, which is why PimsReplayPlayer
    // opts itself out with `data-dd-privacy="hidden"`), and the
    // `title`/`alt`/`aria-label`/`data-*` attributes that would otherwise carry
    // the same content. Nothing opts back out — there is deliberately no
    // `data-dd-privacy="allow"` in this app, so there is no allow-list to keep
    // audited as the UI changes.
    defaultPrivacyLevel: datadog.defaultPrivacyLevel,
    // Without this an action's name is the text of whatever was clicked, which
    // here means pet and owner names: the visit card's label is exactly that.
    enablePrivacyForActionName: true,

    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,

    // Correlates RUM sessions with the Brian API's APM traces. WebSocket
    // connections can't carry tracing headers, so `wsUrl` is deliberately
    // absent — RUM sees those as resources only.
    allowedTracingUrls: [
      (url: string) => {
        try {
          return new URL(url).origin === new URL(apiUrl).origin;
        } catch {
          return false;
        }
      },
    ],

    beforeSend: (event) => {
      if (event.view.url) {
        event.view.url = scrubUrl(event.view.url);
      }
      if (event.view.referrer) {
        event.view.referrer = scrubUrl(event.view.referrer);
      }
      // The LCP element's URL is a view-event field of its own, separate from
      // the matching resource event — and Brian's largest contentful image is
      // often a presigned PIMS screenshot in the audit panel.
      if (event.type === "view" && event.view.performance?.lcp?.resource_url) {
        event.view.performance.lcp.resource_url = scrubUrl(
          event.view.performance.lcp.resource_url,
        );
      }
      if (event.type === "resource" && event.resource.url) {
        event.resource.url = scrubUrl(event.resource.url);
      }
      if (event.type === "error") {
        // On browser-rum 7.x a failed fetch/XHR is reported as a resource
        // event, so `error.resource` is never populated — kept defensively
        // because it is on the SDK's modifiable-field list. The copies of a
        // request URL that actually reach error events ride in `message` and
        // `stack` ("Failed to fetch https://…/visits/<uuid>/…"), so uuids are
        // stripped from those instead.
        if (event.error.resource?.url) {
          event.error.resource.url = scrubUrl(event.error.resource.url);
        }
        event.error.message = scrubText(event.error.message);
        if (event.error.stack) {
          event.error.stack = scrubText(event.error.stack);
        }
        if (event.error.handling_stack) {
          event.error.handling_stack = scrubText(event.error.handling_stack);
        }
      }
      if (event.type === "long_task" && event.long_task.scripts) {
        for (const script of event.long_task.scripts) {
          if (script.source_url) {
            script.source_url = scrubUrl(script.source_url);
          }
          // A LoAF invoker names the element whose listener ran long, and for
          // an id-less element the spec builds that name from its `src`
          // attribute — `VIDEO[src=<presigned url>].onloadedmetadata` is
          // exactly PimsReplayPlayer's video. Not a parseable URL, so the
          // substring scrub rather than `scrubUrl`.
          if (script.invoker) {
            script.invoker = scrubText(script.invoker);
          }
        }
      }
      return true;
    },

    // `router: true` turns OFF automatic view tracking (it sets
    // `trackViewsManually` internally) and hands view creation to the wrapped
    // `createRouter` imported in AppShell. The two are a pair: keep this
    // option while reverting that import to plain `@tanstack/react-router` and
    // RUM records no views at all, with no error to say so. `rumRouter.test.ts`
    // guards the other half.
    plugins: [reactPlugin({ router: true })],
  });

  // Sessions rehydrate from storage while this module's imports evaluate, so
  // the principal is already known here and the first view isn't anonymous.
  // Subscribing only inside this guard keeps the store untouched in
  // environments where RUM is off.
  syncPrincipal();
  unsubscribePrincipal = useSessionStore.subscribe(syncPrincipal);
}
